import { format } from 'date-fns';
import { useCallback } from 'react';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { isAdmin } from 'src/services/auth/auth.utils';
import { TemplateVisibility, EmailMessageTemplate, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

// ----------------------------------------------------------------------

type Props = {
  row: EmailMessageTemplate;
  selected: boolean;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
};

export default function EmailTemplateTableRow({
  row,
  selected,
  onEditRow,
  onDeleteRow,
}: Props) {
  const { t } = useTranslate();
  const authContext = useAuthContext();
  const confirm = useBoolean();
  const popover = usePopover();

  const isCurrentUserAdmin = isAdmin(authContext);

  const getTemplateTypeLabel = useCallback((type: EmailMessageTemplateType) => {
    switch (type) {
      case EmailMessageTemplateType.CANDIDATE_ENGAGEMENT:
        return t('email_sync.templates.template_types.candidate_engagement');
      case EmailMessageTemplateType.CONTACT_ENGAGEMENT:
        return t('email_sync.templates.template_types.contact_engagement');
      case EmailMessageTemplateType.APPLICATION_UPDATE:
        return t('email_sync.templates.template_types.application_update');
      case EmailMessageTemplateType.OFFER_CONTENT:
        return t('email_sync.templates.template_types.offer_content');
      case EmailMessageTemplateType.APPLICATION_REJECTION:
        return t('email_sync.templates.template_types.application_rejection');
      default:
        return type;
    }
  }, [t]);

  const getVisibilityLabel = useCallback((visibility: TemplateVisibility) => {
    switch (visibility) {
      case TemplateVisibility.ORG_WIDE:
        return t('email_sync.templates.visibility_types.org_wide');
      case TemplateVisibility.ACCOUNT_SPECIFIC:
        return t('email_sync.templates.visibility_types.account_specific');
      default:
        return visibility;
    }
  }, [t]);

  const getVisibilityColor = useCallback((visibility: TemplateVisibility) => {
    switch (visibility) {
      case TemplateVisibility.ORG_WIDE:
        return 'primary';
      case TemplateVisibility.ACCOUNT_SPECIFIC:
        return 'default';
      default:
        return 'default';
    }
  }, []);

  const canEdit = isCurrentUserAdmin || row.visibility === TemplateVisibility.ACCOUNT_SPECIFIC;
  const canDelete = isCurrentUserAdmin || row.visibility === TemplateVisibility.ACCOUNT_SPECIFIC;

  return (
    <>
      <TableRow hover selected={selected}>

        <TableCell>
          <Stack spacing={1}>
            <TruncatedTextField
              text={row.name}
              mode="characters"
              limit={50}
              showMoreEnabled={false}
              variant="subtitle2"
              style={{ textTransform: 'capitalize' }}
            />
          </Stack>
        </TableCell>

        <TableCell>
          <Chip
            size="small"
            variant="soft"
            label={getTemplateTypeLabel(row.type)}
            color="info"
          />
        </TableCell>

        <TableCell>
          <Chip
            size="small"
            variant="soft"
            label={getVisibilityLabel(row.visibility)}
            color={getVisibilityColor(row.visibility)}
          />
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {format(new Date(row.created_at), 'dd MMM yyyy')}
          </Typography>
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <IconButton
            color={popover.open ? 'inherit' : 'default'}
            onClick={popover.onOpen}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuList>
          <MenuItem
            onClick={() => {
              onEditRow();
              popover.onClose();
            }}
            disabled={!canEdit}
          >
            <Iconify icon="solar:pen-bold" />
            {t('common.edit')}
          </MenuItem>

          <MenuItem
            onClick={() => {
              confirm.onTrue();
              popover.onClose();
            }}
            disabled={!canDelete}
            sx={{ color: 'error.main' }}
          >
            <Iconify icon="solar:trash-bin-trash-bold" />
            {t('common.delete')}
          </MenuItem>
        </MenuList>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('email_sync.templates.delete_confirm')}
        content={t('email_sync.templates.delete_confirm_description', { name: row.name })}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              onDeleteRow();
              confirm.onFalse();
            }}
          >
            {t('common.delete')}
          </Button>
        }
      />
    </>
  );
}